import { getAuth } from 'firebase/auth';
import { Achievement } from '../../types/goals';
import { generateId } from '../../utils/firebase-helpers';
import { getData, setData, updateData, removeData } from './api-wrapper.service';
import logger from '../../utils/logger';

/**
 * Achievement service.
 *
 * Storage: Firebase Realtime Database at `users/{uid}/achievements/{id}`.
 *
 * NOTE (AH6): this used to target Cloud Firestore via the `firebase/firestore`
 * Web SDK, but the project's default Firestore database is in Datastore Mode,
 * which that SDK cannot talk to — every call threw FAILED_PRECONDITION, so the
 * achievements feature was silently broken in production. It now uses RTDB (the
 * datastore the rest of the app runs on). Owner-scoped paths mean ownership is
 * implicit; the deployed rules already grant the owner read/write on
 * `users/$uid/*`. Public exports and signatures are unchanged.
 */

const userAchievementsPath = (uid: string): string => `users/${uid}/achievements`;
const achievementPath = (uid: string, id: string): string => `${userAchievementsPath(uid)}/${id}`;

/** Returns the current user or throws — every operation is owner-scoped. */
const requireUser = () => {
  const user = getAuth().currentUser;
  if (!user) {
    throw new Error('You must be logged in to access achievements');
  }
  return user;
};

/** Materialize the `{ [id]: data }` RTDB map into an Achievement[] with ids. */
const toAchievementArray = (data: Record<string, any> | null): Achievement[] => {
  if (!data) {
    return [];
  }
  return Object.entries(data).map(([id, value]) => ({ ...(value as Achievement), id }));
};

const sortByDateEarnedDesc = (achievements: Achievement[]): Achievement[] =>
  [...achievements].sort((a, b) => (b.dateEarned || 0) - (a.dateEarned || 0));

/**
 * Get all achievements for the current user
 * @returns Promise with array of achievements
 */
export const getUserAchievements = async (): Promise<Achievement[]> => {
  try {
    const user = requireUser();
    const data = await getData(userAchievementsPath(user.uid));
    return sortByDateEarnedDesc(toAchievementArray(data));
  } catch (error) {
    logger.error('Error getting user achievements:', error);
    throw error;
  }
};

/**
 * Get recent achievements for the current user
 * @param count Number of achievements to fetch
 * @returns Promise with array of achievements
 */
export const getRecentAchievements = async (count: number = 5): Promise<Achievement[]> => {
  try {
    const achievements = await getUserAchievements();
    return achievements.slice(0, count);
  } catch (error) {
    logger.error('Error getting recent achievements:', error);
    throw error;
  }
};

/**
 * Get achievements by category
 * @param category Achievement category to filter by
 * @returns Promise with array of achievements
 */
export const getAchievementsByCategory = async (category: string): Promise<Achievement[]> => {
  try {
    const achievements = await getUserAchievements();
    return achievements.filter(achievement => achievement.category === category);
  } catch (error) {
    logger.error(`Error getting achievements for category ${category}:`, error);
    throw error;
  }
};

/**
 * Get a single achievement by ID (for the current user)
 * @param id Achievement ID
 * @returns Promise with achievement or null if not found
 */
export const getAchievement = async (id: string): Promise<Achievement | null> => {
  try {
    const user = requireUser();
    const value = await getData(achievementPath(user.uid, id));
    return value ? { ...(value as Achievement), id } : null;
  } catch (error) {
    logger.error(`Error getting achievement ${id}:`, error);
    throw error;
  }
};

/**
 * Create a new achievement
 * @param achievementData Achievement data (without ID)
 * @returns Promise with the created achievement ID
 */
export const createAchievement = async (
  achievementData: Omit<Achievement, 'id' | 'dateEarned'>
): Promise<string> => {
  try {
    const user = requireUser();

    const achievementId = generateId();
    const timestamp = Date.now();

    const achievementToCreate: Achievement = {
      ...achievementData,
      id: achievementId,
      userId: user.uid,
      dateEarned: timestamp,
      isNew: true,
      shareCount: 0,
      unlockedAt: timestamp
    };

    await setData(achievementPath(user.uid, achievementId), achievementToCreate);

    return achievementId;
  } catch (error) {
    logger.error('Error creating achievement:', error);
    throw error;
  }
};

/**
 * Update an existing achievement
 * @param id Achievement ID
 * @param data Updated achievement data
 */
export const updateAchievement = async (
  id: string,
  data: Partial<Achievement>
): Promise<void> => {
  try {
    const user = requireUser();

    // Verify the achievement exists and belongs to the user
    const achievement = await getAchievement(id);
    if (!achievement) {
      throw new Error(`Achievement with ID ${id} not found`);
    }
    if (achievement.userId !== user.uid) {
      throw new Error('You can only update your own achievements');
    }

    // Never rewrite the id via a merge update
    const { id: _id, ...updateData_ } = data;

    await updateData(achievementPath(user.uid, id), {
      ...updateData_,
      updatedAt: Date.now()
    });
  } catch (error) {
    logger.error(`Error updating achievement ${id}:`, error);
    throw error;
  }
};

/**
 * Delete an achievement
 * @param id Achievement ID
 */
export const deleteAchievement = async (id: string): Promise<void> => {
  try {
    const user = requireUser();

    const achievement = await getAchievement(id);
    if (!achievement) {
      throw new Error(`Achievement with ID ${id} not found`);
    }
    if (achievement.userId !== user.uid) {
      throw new Error('You can only delete your own achievements');
    }

    await removeData(achievementPath(user.uid, id));
  } catch (error) {
    logger.error(`Error deleting achievement ${id}:`, error);
    throw error;
  }
};

/**
 * Mark an achievement as viewed (not new)
 * @param id Achievement ID
 */
export const markAchievementViewed = async (id: string): Promise<void> => {
  try {
    const user = requireUser();
    await updateData(achievementPath(user.uid, id), {
      isNew: false,
      updatedAt: Date.now()
    });
  } catch (error) {
    logger.error(`Error marking achievement ${id} as viewed:`, error);
    throw error;
  }
};

/**
 * Increment an achievement's share count
 * @param id Achievement ID
 */
export const incrementAchievementShareCount = async (id: string): Promise<void> => {
  try {
    const user = requireUser();

    const achievement = await getAchievement(id);
    if (!achievement) {
      throw new Error(`Achievement with ID ${id} not found`);
    }
    if (achievement.userId !== user.uid) {
      throw new Error('You can only update your own achievements');
    }

    await updateData(achievementPath(user.uid, id), {
      shareCount: (achievement.shareCount || 0) + 1,
      updatedAt: Date.now()
    });
  } catch (error) {
    logger.error(`Error incrementing share count for achievement ${id}:`, error);
    throw error;
  }
};

/**
 * Check if a user has a specific achievement
 * @param achievementType Type of achievement to check
 * @param category Category of achievement to check
 * @returns Promise with boolean indicating if user has the achievement
 */
export const hasAchievement = async (
  achievementType: string,
  category: string
): Promise<boolean> => {
  try {
    const achievements = await getUserAchievements();
    return achievements.some(
      achievement =>
        achievement.achievementType === achievementType &&
        achievement.category === category
    );
  } catch (error) {
    logger.error(`Error checking if user has achievement ${achievementType}:`, error);
    throw error;
  }
};

/**
 * Get achievement statistics for a user
 * @returns Promise with achievement statistics
 */
export const getAchievementStats = async (): Promise<{
  total: number;
  byCategory: Record<string, number>;
  recentlyEarned: Achievement[];
  mostValuable: Achievement[];
}> => {
  try {
    const achievements = await getUserAchievements();

    // Calculate statistics
    const total = achievements.length;

    // Count by category
    const byCategory: Record<string, number> = {};
    achievements.forEach(achievement => {
      if (!byCategory[achievement.category]) {
        byCategory[achievement.category] = 0;
      }
      byCategory[achievement.category]++;
    });

    // Get recently earned achievements
    const recentlyEarned = [...achievements]
      .sort((a, b) => b.dateEarned - a.dateEarned)
      .slice(0, 5);

    // Get most valuable achievements (by points)
    const mostValuable = [...achievements]
      .sort((a, b) => (b.points || 0) - (a.points || 0))
      .slice(0, 5);

    return {
      total,
      byCategory,
      recentlyEarned,
      mostValuable
    };
  } catch (error) {
    logger.error('Error getting achievement statistics:', error);
    throw error;
  }
};
